import { Alert, Box, Button, Card, CardContent, Typography } from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import type { ConversationMessage } from "@/types";

interface ConfirmationCardProps {
  message: ConversationMessage;
  onApprove: () => void;
  onReject: () => void;
  busy?: boolean;
}

/**
 * Renders a pending write-tool invocation as an Approve/Reject card.
 * Only shown when message.tool_invocation_status === 'pending'.
 */
export default function ConfirmationCard({
  message,
  onApprove,
  onReject,
  busy,
}: ConfirmationCardProps) {
  const args = (message.tool_arguments ?? {}) as Record<string, unknown>;

  return (
    <Card sx={{ my: 1, borderLeft: 4, borderColor: "warning.main" }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          About to run {message.tool_name}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: 2,
            rowGap: 0.5,
            mb: 2,
          }}
        >
          {Object.entries(args).map(([k, v]) => (
            <Box key={k} sx={{ display: "contents" }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {k}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {typeof v === "string" ? v : JSON.stringify(v)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This action will affect external systems. Approve only if the parameters above look right.
        </Alert>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Close />}
            onClick={onReject}
            disabled={busy}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Check />}
            onClick={onApprove}
            disabled={busy}
          >
            {busy ? "Running…" : "Approve & run"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
